/**
 * バナーレイアウトエディタのモーダル (docs/features/banner-layout-template-plan.md §7)。
 *
 * 左: テンプレート (プリセット / マイテンプレート の2列グリッド)、中央: ツールバー (アスペクト比・背景の分割・パーツ追加・元に戻す / やり直す) と
 * キャンバス、右: パーツを追加 (§21 で右パネルのインスペクタを廃止し、要素の設定はキャンバス上のフローティング操作列 BannerLayoutElementToolbar へ)、
 * 下: キャンセル / マイテンプレートとして保存 / 適用。保存は、マイテンプレートを読み込んでいなければ単独ボタン「マイテンプレートとして保存」(押すと名前入力)、
 * 読み込んでいれば「『名前』を上書き保存」(押すと即上書き) + シェブロン (名前をつけて保存…) の分割ボタン (§23)
 * (2026-10-18 使いやすさ改善。旧: 上段に横スクロールのカード列・左にパレット。保存欄は一度マイテンプレートの下に置いたが利用者指示 2026-10-19 でフッターへ戻した)。
 * 背景の分割はツールバーのアイコン (現在の設定を描く) → ポップオーバーで設定し、分割線はキャンバス上のドラッグでも動かせる (§20・2026-10-19)。
 * 構造は既存モーダル (MotionLayerModal 等) の modal-overlay / modal-card に倣う。
 * キー操作: Cmd/Ctrl+Z 元に戻す・Shift+Cmd/Ctrl+Z / Cmd/Ctrl+Y やり直す・Cmd/Ctrl+D 複製・Delete 削除・矢印で移動。
 *
 * レイアウトは配置だけを持ち、文言は入力させない (§13)。「テキスト」アコーディオンの欄の値が同じ種類の最初の枠に入るので、
 * キャンバスとインスペクタにはその値を表示だけする (textSync.ts)。
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LayoutDashboard, LayoutTemplate, Redo2, Share, Undo2, UserRoundPen } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isComposingEnter } from '../../../features/videoEditor/components/imeKey';
import {
  createBannerLayoutTemplate,
  deleteBannerLayoutTemplate,
  fetchBannerLayoutTemplates,
  shareBannerLayoutTemplate,
  updateBannerLayoutTemplate,
} from '../../../features/bannerLayout/api';
import { CLOUD_QUERY_KEY } from '../../../features/materials/hooks';
import { ratiosMatch } from '../../../features/bannerLayout/effectiveAspectRatio';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ChevronIcon } from '../../common/selectIcons';
import { AspectRatioIcon } from '../../common/AspectRatioIcon';
import HoverPillButton from '../../common/HoverPillButton';
import { Tooltip } from '../../common/Tooltip';
import { createElement, newElementId, moveRect } from '../../../features/bannerLayout/geometry';
import { BANNER_LAYOUT_PRESETS, instantiateLayout } from '../../../features/bannerLayout/presets';
import { BANNER_LAYOUT_ROLES, elementLabels, type BannerLayoutKind } from '../../../features/bannerLayout/roles';
import { assignedLayoutTexts, isFirstOfKind, stripLayoutTexts } from '../../../features/bannerLayout/textSync';
import {
  BANNER_LAYOUT_MAX_ELEMENTS,
  BANNER_LAYOUT_WARN_ELEMENTS,
  bannerLayoutSchema,
  type BannerLayout,
  type BannerLayoutBackground,
  type BannerLayoutElement,
  type BannerLayoutTemplate,
} from '../../../features/bannerLayout/types';
import { BannerLayoutCanvas, canvasDims, fitCanvasSize } from './BannerLayoutCanvas';
import { type InspectorAsset } from './BannerLayoutControls';
import { BannerLayoutElementToolbar } from './BannerLayoutElementToolbar';
import { BannerLayoutPalette } from './BannerLayoutPalette';
import { BannerLayoutSplitButton } from './BannerLayoutSplitButton';
import { Popover } from '../../../features/videoEditor/components/Popover';
import { EditorSelect } from '../../../features/videoEditor/components/EditorSelect';
import { useEscapeCapture } from '../../../features/bannerLayout/useEscapeCapture';
import styles from './BannerLayout.module.css';

/** エディタで選べる比率 (比率が媒体規格・目標解像度で固定されていないとき) */
// プリセットを用意している比率 (presets.json と同期)。ドロップダウンはこの順 (利用者指定 2026-10-18: 6:5 → 1:1 → 1.91:1 → 4:5 → 16:9 → 9:16 → それ以外)。
// 6:5 = 300×250 / 336×280 のレクタングルバナー、4:1 / 8:1 = 横長バナー、1:4 / 1:8 = 縦長バナー
const RATIO_CHOICES = ['6:5', '1:1', '1.91:1', '4:5', '16:9', '9:16', '3:2', '2:3', '3:4', '4:1', '8:1', '1:4', '1:8'];
const RATIO_OPTION_LABELS: Record<string, string> = { '6:5': '6:5 (300×250)' };
/** RATIO_CHOICES の順に並べる。一覧に無い比率 (実効比率・テンプレートの比率) はその後ろに出現順 */
function sortRatioOptions(options: string[]): string[] {
  const rank = (r: string) => {
    const i = RATIO_CHOICES.findIndex((c) => ratiosMatch(c, r));
    return i === -1 ? RATIO_CHOICES.length : i;
  };
  return options.map((r, i) => ({ r, i })).sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i).map((x) => x.r);
}
const TEMPLATES_QUERY_KEY = ['banner-layout-templates'];
/** 元に戻す の段数の上限 */
const HISTORY_LIMIT = 50;
/** テンプレートカードのサムネイルの長辺 (px・素材パネルのカードの画像部と同じ 150) と、キャンバスの長辺の上限 (px) */
const CARD_THUMB_SIZE = 150;
const CANVAS_MAX_SIZE = 760;
type EditorSnapshot = { elements: BannerLayoutElement[]; background: BannerLayoutBackground | undefined };

export type BannerLayoutEditorModalProps = {
  /** 適用済みのレイアウト (無ければ新規作成) */
  initialLayout: BannerLayout | null;
  /** 適用済みレイアウトの名前 (読み込んだプリセット / テンプレートの名前) */
  initialName?: string | null;
  /** 「テキスト」アコーディオンの値 (settings.attributes)。枠に入る文言の表示に使う */
  attributes: Record<string, unknown>;
  /** 生成時の実効比率 (null = 指定なし → 1:1 で開く) */
  effectiveAspect: string | null;
  /** 媒体規格・目標解像度で比率が決まっている (エディタで別の比率にして適用すると、それらの指定を外す) */
  ratioLocked: boolean;
  assets: InspectorAsset[];
  logoBrandNames: string[];
  excludeText: boolean;
  /** name は読み込んだプリセット / テンプレートの名前 (白紙から作ったら null) */
  onApply: (layout: BannerLayout, name: string | null) => void;
  onClose: () => void;
};

export function BannerLayoutEditorModal({
  initialLayout,
  initialName = null,
  attributes,
  effectiveAspect,
  ratioLocked,
  assets,
  logoBrandNames,
  excludeText,
  onApply,
  onClose,
}: BannerLayoutEditorModalProps) {
  const titleId = useId();
  const aspectLabelId = useId();
  const [aspect, setAspect] = useState<string>(effectiveAspect ?? initialLayout?.aspectRatio ?? '1:1');
  // 生成のアスペクト比 (現在の設定) と違う比率で適用するときの確認
  const [pendingApply, setPendingApply] = useState<BannerLayout | null>(null);
  // マイテンプレートの 上書き保存 / チームに共有 / 削除 の確認 (アプリ内の ConfirmDialog。window.confirm はネイティブダイアログが
  // 無効な環境 (アプリ内ブラウザ等) で常にキャンセル扱いになり、押しても何も起きなかった 2026-10-19)
  const [pendingTemplateAction, setPendingTemplateAction] = useState<{ kind: 'share' | 'delete'; template: BannerLayoutTemplate } | null>(null);
  // 文言はレイアウトに持たない (§13)。段階1の適用済みレイアウトに残る text は読み込み時に外す
  const [elements, setElements] = useState<BannerLayoutElement[]>(() => stripLayoutTexts(initialLayout?.elements ?? []));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 背景の分割 (レイアウト全体の属性・§16)
  const [background, setBackground] = useState<BannerLayoutBackground | undefined>(initialLayout?.background);
  // 読み込んだプリセット / テンプレートの名前 (入口の表示用。読み込み後に手を入れても名前は保つ)
  const [loadedName, setLoadedName] = useState<string | null>(initialName);
  // このセッションで読み込んだマイテンプレートの id (上書き保存の対象)。プリセットを読み込んだら null に戻す
  const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(null);
  // 読み込んだカード (preset:<id> / template:<id>)。カードに選択中の枠線を出し続ける (利用者指示 2026-10-19)
  const [loadedCardKey, setLoadedCardKey] = useState<string | null>(null);
  // 元に戻す / やり直す (要素と背景の分割のスナップショット。ドラッグは1回で1段・最大50段)
  const [past, setPast] = useState<EditorSnapshot[]>([]);
  const [future, setFuture] = useState<EditorSnapshot[]>([]);
  const [tab, setTab] = useState<'presets' | 'templates'>('presets');
  const [saveName, setSaveName] = useState('');
  // 「テンプレートとして保存」(フッター・§23): マイテンプレートを読み込んでいなければ単独ボタンで名前入力のポップオーバーを開く。
  // 読み込んでいれば本体が「『名前』を上書き保存」(即保存) で、シェブロンのメニューに「名前をつけて保存…」
  // (カードごとの「上書き保存」は、無関係なテンプレートを今の内容で潰しやすいので外した)
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const saveAnchorRef = useRef<HTMLDivElement>(null);
  const closeSaveMenu = useCallback(() => setSaveMenuOpen(false), []);
  const closeSave = useCallback(() => setSaveOpen(false), []);
  useEscapeCapture(saveMenuOpen, closeSaveMenu);
  useEscapeCapture(saveOpen, closeSave);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // キャンバスは空き領域 (stage) に収まる大きさにする (画面が低いと固定 560px ではカード列・フッターに重なる)
  // 要素が付け替わっても (再マウント等) いまの要素を測るよう、callback ref で ResizeObserver を付け直す
  const [stage, setStage] = useState<{ width: number; height: number } | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const stageRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setStage({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  const labels = elementLabels(elements);
  const canvasSize = fitCanvasSize(aspect, stage, CANVAS_MAX_SIZE);
  const assignedTexts = useMemo(() => assignedLayoutTexts(elements, attributes), [elements, attributes]);
  const selected = elements.find((e) => e.id === selectedId) ?? null;
  const selectedIndex = selected ? elements.indexOf(selected) : -1;

  const templatesQuery = useQuery({ queryKey: TEMPLATES_QUERY_KEY, queryFn: fetchBannerLayoutTemplates, staleTime: 60_000 });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
  const errorMessage = (e: unknown, fallback: string) => {
    const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
    return msg || fallback;
  };
  const createMutation = useMutation({
    mutationFn: ({ name, layout }: { name: string; layout: BannerLayout }) => createBannerLayoutTemplate(name, layout),
    onSuccess: (template) => {
      setSaveName('');
      setSaveOpen(false);
      setNotice('マイテンプレートに保存しました');
      setTab('templates');
      // 以後の「上書き保存」は保存したばかりのテンプレートを対象にする (元の読み込み元を潰さない・§23)
      if (template?.id) {
        setLoadedTemplateId(template.id);
        setLoadedCardKey(`template:${template.id}`);
        setLoadedName(template.name);
      }
      void invalidate();
    },
    onError: (e) => setError(errorMessage(e, 'テンプレートを保存できませんでした')),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, layout }: { id: string; layout: BannerLayout }) => updateBannerLayoutTemplate(id, { layout }),
    onSuccess: () => {
      setNotice('テンプレートを上書き保存しました');
      void invalidate();
    },
    onError: (e) => setError(errorMessage(e, 'テンプレートを上書きできませんでした')),
  });
  // チーム共有 (段階2): ガイド PNG を共有フォルダの素材にする。共有するのは保存済みのテンプレート (編集中の内容ではない)
  const shareMutation = useMutation({
    mutationFn: (id: string) => shareBannerLayoutTemplate(id),
    onSuccess: () => {
      setNotice('共有フォルダに画像として保存しました。チームの人は素材から選んで使えます');
      void queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
    },
    onError: (e) => setError(errorMessage(e, 'テンプレートを共有できませんでした')),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBannerLayoutTemplate(id),
    onSuccess: () => void invalidate(),
    onError: (e) => setError(errorMessage(e, 'テンプレートを削除できませんでした')),
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // 確認ダイアログが開いているときはダイアログを閉じるだけ
      if (pendingApply || pendingTemplateAction) {
        setPendingApply(null);
        setPendingTemplateAction(null);
        return;
      }
      // 文言・名前の入力中の Esc は入力の取り消しに譲り、モーダルは閉じない
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, pendingApply, pendingTemplateAction]);

  // onlyIds: 取り込む要素 (パーツの追加では追加した要素だけ。プリセット / テンプレートの読み込みでは全要素が新しい)

  const snapshot = (): EditorSnapshot => ({ elements, background });
  const pushHistory = (prev: EditorSnapshot = snapshot()) => {
    setPast((p) => [...p.slice(-(HISTORY_LIMIT - 1)), prev]);
    setFuture([]);
  };
  const restore = (snap: EditorSnapshot) => {
    setElements(snap.elements);
    setBackground(snap.background);
    if (selectedId && !snap.elements.some((e) => e.id === selectedId)) setSelectedId(null);
  };
  const undo = () => {
    if (past.length === 0) return;
    const prev = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture((f) => [...f, snapshot()]);
    restore(prev);
  };
  const redo = () => {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    setFuture(future.slice(0, -1));
    setPast((p) => [...p, snapshot()]);
    restore(next);
  };
  /** 要素を更新する。history=false はドラッグ中の連続更新 (履歴はドラッグ開始時に1回積む) */
  const updateElements = (next: BannerLayoutElement[], history = true) => {
    setError(null);
    setNotice(null);
    if (history) pushHistory();
    setElements(next);
  };
  const updateBackground = (next: BannerLayoutBackground | undefined) => {
    setError(null);
    setNotice(null);
    pushHistory();
    setBackground(next);
  };

  const duplicateSelected = () => {
    if (!selected || elements.length >= BANNER_LAYOUT_MAX_ELEMENTS) return;
    const copy = { ...moveRect(selected, 0.02, 0.02), id: newElementId() };
    updateElements([...elements, copy]);
    setSelectedId(copy.id);
  };
  // 元に戻す / やり直す / 複製のキー操作 (入力欄の中では効かせない)
  const keyActionsRef = useRef({ undo, redo, duplicateSelected });
  keyActionsRef.current = { undo, redo, duplicateSelected };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) return;
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) keyActionsRef.current.redo();
        else keyActionsRef.current.undo();
      } else if (key === 'y') {
        e.preventDefault();
        keyActionsRef.current.redo();
      } else if (key === 'd') {
        e.preventDefault();
        keyActionsRef.current.duplicateSelected();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const addElement = (kind: BannerLayoutKind) => {
    if (elements.length >= BANNER_LAYOUT_MAX_ELEMENTS) return;
    const el = createElement(kind, elements);
    updateElements([...elements, el]);
    setSelectedId(el.id);
  };

  const loadLayout = (
    source: { aspectRatio: string; elements: BannerLayoutElement[]; background?: BannerLayoutBackground },
    name: string,
    card: { preset: string } | { template: string },
  ) => {
    // 一覧は選んでいるアスペクト比で絞り込んでいるので、そのままキャンバスの比率で読み込む
    const loaded = instantiateLayout(source, aspect, assets);
    pushHistory();
    updateElements(stripLayoutTexts(loaded.elements), false);
    setBackground(loaded.background);
    setLoadedName(name);
    setLoadedTemplateId('template' in card ? card.template : null);
    setLoadedCardKey('template' in card ? `template:${card.template}` : `preset:${card.preset}`);
    setSelectedId(null);
  };

  const replaceElement = (next: BannerLayoutElement) => updateElements(elements.map((e) => (e.id === next.id ? next : e)));
  const deleteElement = (id: string) => {
    updateElements(elements.filter((e) => e.id !== id));
    if (selectedId === id) setSelectedId(null);
  };


  const currentLayout = (): BannerLayout => ({ version: 1, aspectRatio: aspect, elements, ...(background ? { background } : {}) });
  const validate = (): BannerLayout | null => {
    const parsed = bannerLayoutSchema.safeParse(currentLayout());
    if (!parsed.success) {
      setError(elements.length === 0 ? 'パーツを1つ以上置いてください' : parsed.error.issues[0]?.message ?? 'レイアウトが不正です');
      return null;
    }
    return parsed.data;
  };

  // 商品枠は「選択中の画像の何枚目か」を指すので、画像が無くても適用できる (生成時に BE が枠を外す・§17)
  const handleApply = () => {
    const layout = validate();
    if (!layout) return;
    // 現在の設定 (生成のアスペクト比) と違う比率なら確認する。「指定なし」のときは上書きするものが無いので確認しない
    if (effectiveAspect && !ratiosMatch(layout.aspectRatio, effectiveAspect)) {
      setPendingApply(layout);
      return;
    }
    onApply(layout, loadedName);
  };
  const handleSave = () => {
    const name = saveName.trim();
    if (!name) {
      setError('テンプレート名を入力してください');
      return;
    }
    const layout = validate();
    if (layout) createMutation.mutate({ name, layout });
  };

  // プリセット・マイテンプレートは選んでいるアスペクト比のものだけを出す
  const presets = useMemo(() => BANNER_LAYOUT_PRESETS.filter((p) => ratiosMatch(p.aspectRatio, aspect)), [aspect]);
  const allTemplates: BannerLayoutTemplate[] = templatesQuery.data ?? [];
  // 読み込んだマイテンプレートが今もあれば上書き保存の対象 (削除済みなら出さない)
  const loadedTemplate = loadedTemplateId ? allTemplates.find((t) => t.id === loadedTemplateId) ?? null : null;
  /** 読み込んだマイテンプレートを今の内容で上書きする (確認なし・エディタの「保存」と同じ扱い・§23) */
  const handleOverwrite = () => {
    if (!loadedTemplate) return;
    setError(null);
    setNotice(null);
    const layout = validate();
    if (layout) updateMutation.mutate({ id: loadedTemplate.id, layout });
  };
  const openSaveName = () => {
    setError(null);
    setNotice(null);
    setSaveMenuOpen(false);
    setSaveOpen((v) => !v);
  };
  const templates = allTemplates.filter((t) => ratiosMatch(t.aspect_ratio, aspect));
  // 並び: 現在の設定 (生成のアスペクト比) → 定番の比率 → 保存したテンプレートの比率 → 選択中 (どれにも無いとき)。
  // 選択を変えても並びは動かさない。同じ比率の表記ゆれは1つにまとめる
  const ratioOptions = sortRatioOptions(
    [...RATIO_CHOICES, ...(effectiveAspect ? [effectiveAspect] : []), ...allTemplates.map((t) => t.aspect_ratio), aspect].reduce<string[]>(
      (acc, r) => (acc.some((x) => ratiosMatch(x, r)) ? acc : [...acc, r]),
      [],
    ),
  );
  const notes: string[] = [];
  if (elements.length > BANNER_LAYOUT_WARN_ELEMENTS) notes.push('要素が多すぎると、AIがレイアウトを守りにくくなります');
  if (excludeText && elements.some((e) => BANNER_LAYOUT_ROLES[e.kind].hasText)) notes.push('「テキストを含めない」がオンのため、文字の要素は使われません');

  const templateCards = (
    <>
      {templatesQuery.isLoading && <span className={styles.emptyNote}>読み込み中…</span>}
      {templatesQuery.isError && <span className={styles.emptyNote}>テンプレートを読み込めませんでした</span>}
      {templatesQuery.isSuccess && allTemplates.length === 0 && (
        <span className={styles.emptyNote}>保存したテンプレートはまだありません。下の「テンプレートとして保存」で保存できます</span>
      )}
      {templatesQuery.isSuccess && allTemplates.length > 0 && templates.length === 0 && (
        <span className={styles.emptyNote}>このアスペクト比のテンプレートはありません (他のアスペクト比に{allTemplates.length}件)</span>
      )}
      {templates.map((t) => (
        <div
          key={t.id}
          className={`${styles.layoutCard} ${loadedCardKey === `template:${t.id}` ? styles.layoutCardSelected : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={loadedCardKey === `template:${t.id}`}
          onClick={() => loadLayout(t.layout, t.name, { template: t.id })}
          onKeyDown={(e) => { if (e.key === 'Enter' && !isComposingEnter(e)) loadLayout(t.layout, t.name, { template: t.id }); }}
          data-testid={`banner-layout-template-${t.id}`}
        >
          {/* 削除は素材カードと同じ、カード右上のホバーで出るゴミ箱 (HoverPillButton・利用者指示 2026-10-19) */}
          <span className={styles.layoutCardTopRight}>
            <HoverPillButton
              variant="danger"
              label="削除"
              icon={<img src="/bin_1_white.svg" alt="" />}
              onClick={(e) => {
                e.stopPropagation();
                setPendingTemplateAction({ kind: 'delete', template: t });
              }}
            />
          </span>
          <span className={styles.layoutCardThumb}><BannerLayoutCanvas aspectRatio={t.aspect_ratio} elements={t.layout.elements} background={t.layout.background} productImages={assets} size={CARD_THUMB_SIZE} /></span>
          {/* 下の帯は素材カードのメタ情報 (バッジの行) と同じ見た目: 名前をバッジ風に、右端に共有アイコン */}
          <span className={styles.layoutCardMeta}>
            <span className={styles.layoutCardName}>{t.name}</span>
            {/* チームに共有は一般的な共有アイコン (上向き矢印・lucide share) だけにする (利用者指示 2026-10-19) */}
            <Tooltip content="チームに共有 (レイアウトの画像を共有フォルダに保存します。編集はできません)" placement="bottom">
              <button
                type="button"
                className={`${styles.iconButton} ${styles.cardIconButton}`}
                disabled={shareMutation.isPending}
                aria-label="チームに共有"
                onClick={(e) => {
                  e.stopPropagation();
                  setError(null);
                  setNotice(null);
                  setPendingTemplateAction({ kind: 'share', template: t });
                }}
              >
                <Share size={17} aria-hidden="true" />
              </button>
            </Tooltip>
          </span>
        </div>
      ))}
    </>
  );

  return createPortal(
    <div
      className="modal-overlay"
      data-testid="banner-layout-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`modal-card ${styles.modalCard}`} role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid="banner-layout-modal">
        <div className="modal-header">
          <h3 className={`modal-title ${styles.titleWithIcon}`} id={titleId}>
            {/* 入口 (BannerLayoutEntry) と同じ lucide の layout-dashboard */}
            <LayoutDashboard size={18} className={styles.titleIcon} aria-hidden="true" />
            レイアウトエディタ
          </h3>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">✕</button>
        </div>
        <div className={styles.editorGrid}>
          {/* 左: テンプレート (プリセット / マイテンプレート) */}
          <aside className={styles.sidePanel} data-testid="banner-layout-templates-panel">
            <div className={styles.tabs} role="tablist">
              {/* 狭い幅 (1200px 以下) では文字を隠してアイコン + 件数だけにする (利用者指摘 2026-10-18: 文字がはみ出す)。名前は aria-label / title で残す */}
              <Tooltip content="プリセット (運営が用意した用途別のレイアウト)" placement="bottom">
                <button type="button" role="tab" aria-selected={tab === 'presets'} aria-label="プリセット" className={`${styles.tab} ${tab === 'presets' ? styles.tabActive : ''}`} onClick={() => setTab('presets')}>
                  <LayoutTemplate size={14} className={styles.tabIcon} aria-hidden="true" />
                  <span className={styles.tabText}>プリセット</span>
                  <span className={styles.tabCount}>{presets.length}</span>
                </button>
              </Tooltip>
              <Tooltip content="マイテンプレート (自分で保存したレイアウト)" placement="bottom">
                <button type="button" role="tab" aria-selected={tab === 'templates'} aria-label="マイテンプレート" className={`${styles.tab} ${tab === 'templates' ? styles.tabActive : ''}`} onClick={() => setTab('templates')}>
                  <UserRoundPen size={14} className={styles.tabIcon} aria-hidden="true" />
                  <span className={styles.tabText}>マイテンプレート</span>
                  {templatesQuery.isSuccess && <span className={styles.tabCount}>{templates.length}</span>}
                </button>
              </Tooltip>
            </div>
            <div className={styles.cardGrid}>
              {tab === 'presets' && presets.length === 0 && (
                <span className={styles.emptyNote}>このアスペクト比のプリセットはありません</span>
              )}
              {tab === 'presets' &&
                presets.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`${styles.layoutCard} ${loadedCardKey === `preset:${p.id}` ? styles.layoutCardSelected : ''}`}
                    aria-pressed={loadedCardKey === `preset:${p.id}`}
                    onClick={() => loadLayout(p.layout, p.name, { preset: p.id })}
                    title={`${p.name} (${p.aspectRatio})`}
                    data-testid={`banner-layout-preset-${p.id}`}
                  >
                    <span className={styles.layoutCardThumb}><BannerLayoutCanvas aspectRatio={p.aspectRatio} elements={p.layout.elements} background={p.layout.background} size={CARD_THUMB_SIZE} /></span>
                    <span className={styles.layoutCardMeta}><span className={styles.layoutCardName}>{p.name}</span></span>
                  </button>
                ))}
              {tab === 'templates' && templateCards}
            </div>
          </aside>

          {/* 中央: ツールバー + キャンバス */}
          <div className={styles.centerColumn}>
            <div className={styles.toolbar}>
              {/* ラベルとドロップダウンは2行 (利用者指示 2026-10-19)。ドロップダウンは動画エディタと同じカスタムの EditorSelect
                  (ネイティブ select をやめ、高さ 28px を背景の分割・元に戻す のアイコンと揃える) */}
              <div className={`${styles.toolbarField} ${styles.aspectBar}`}>
                <span id={aspectLabelId}>アスペクト比</span>
                <EditorSelect
                  label="アスペクト比"
                  value={ratioOptions.find((r) => ratiosMatch(r, aspect)) ?? aspect}
                  options={ratioOptions.map((r) => ({
                    value: r,
                    label: RATIO_OPTION_LABELS[r] ?? r,
                    // 比率の形が一目で分かるよう、文言の前に比率のSVG (利用者指示 2026-10-21)。読み上げは文言だけで足りるのでアイコンは隠す
                    icon: <span className={styles.ratioIcon} aria-hidden="true"><AspectRatioIcon ratio={r} size={16} /></span>,
                  }))}
                  onChange={setAspect}
                  inModal
                />
              </div>
              {/* アスペクト比と同じ「ラベル + 操作」の2行 (利用者指示 2026-10-19) */}
              <div className={styles.toolbarField}>
                <span>背景の分割</span>
                <BannerLayoutSplitButton background={background} aspectRatio={aspect} onChange={updateBackground} />
              </div>
              <div className={styles.historyButtons}>
                <button type="button" className={styles.iconButton} onClick={undo} disabled={past.length === 0} aria-label="元に戻す" title="元に戻す (Cmd/Ctrl+Z)">
                  <Undo2 size={16} aria-hidden="true" />
                </button>
                <button type="button" className={styles.iconButton} onClick={redo} disabled={future.length === 0} aria-label="やり直す" title="やり直す (Shift+Cmd/Ctrl+Z)">
                  <Redo2 size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
            <div ref={stageRef} className={styles.canvasStage}>
              {/* キャンバスと同じ大きさの枠に、選択中の要素のフローティング操作列を重ねる (§21) */}
              <div className={styles.canvasFrame} style={canvasDims(aspect, canvasSize)}>
                <BannerLayoutCanvas
                  aspectRatio={aspect}
                  elements={elements}
                  productImages={assets}
                  texts={excludeText ? undefined : assignedTexts}
                  background={background}
                  size={canvasSize}
                  interactive
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onChange={(next) => updateElements(next, false)}
                  onBackgroundChange={setBackground}
                  onDragStart={() => pushHistory()}
                  onDelete={deleteElement}
                />
                {selected && (
                  <BannerLayoutElementToolbar
                    key={selected.id}
                    element={selected}
                    label={labels[selectedIndex]}
                    canvasWidth={canvasDims(aspect, canvasSize).width}
                    canvasHeight={canvasDims(aspect, canvasSize).height}
                    assets={assets}
                    logoBrandNames={logoBrandNames}
                    excludeText={excludeText}
                    firstOfKind={isFirstOfKind(elements, selected)}
                    assignedText={assignedTexts[selected.id] ?? ''}
                    onChange={replaceElement}
                    onDuplicate={duplicateSelected}
                    onDelete={() => deleteElement(selected.id)}
                  />
                )}
              </div>
            </div>
            {notes.length > 0 && <div className={styles.notes}>{notes.map((n) => <span key={n}>{n}</span>)}</div>}
          </div>

          {/* 右: パーツを追加 (§21: 旧インスペクタの位置) */}
          <div className={styles.paletteColumn}>
            <BannerLayoutPalette
              variant="column"
              onAdd={addElement}
              full={elements.length >= BANNER_LAYOUT_MAX_ELEMENTS}
              disabledKinds={{}}
            />
          </div>
        </div>
        <div className={styles.footer}>
          {/* 並び (利用者指示 2026-10-19): キャンセルは左端、右に テンプレートとして保存 → 適用。メッセージは中央 */}
          <button type="button" className="btn-cancel" onClick={onClose}>キャンセル</button>
          <div className={styles.footerMessage}>
            {error && <span className={styles.error} role="alert">{error}</span>}
            {!error && notice && <span className={styles.inspectorHint}>{notice}</span>}
          </div>
          <div className={styles.footerRight}>
            <div ref={saveAnchorRef} className={styles.footerSaveAnchor}>
              {loadedTemplate ? (
                // 読み込んだマイテンプレートがある: 本体 = 上書き保存 (即保存)、シェブロン = 名前をつけて保存… (§23)
                <span className={styles.saveSplit}>
                  <button
                    type="button"
                    className={`btn-modal-secondary ${styles.saveSplitMain}`}
                    onClick={handleOverwrite}
                    disabled={createMutation.isPending || updateMutation.isPending}
                    title={`「${loadedTemplate.name}」を今の内容で上書き保存します`}
                    data-testid="banner-layout-save-overwrite"
                  >
                    「{loadedTemplate.name}」を上書き保存
                  </button>
                  <button
                    type="button"
                    className={`btn-modal-secondary ${styles.saveSplitChevron}`}
                    onClick={() => {
                      setSaveOpen(false);
                      setSaveMenuOpen((v) => !v);
                    }}
                    aria-label="その他の保存"
                    aria-haspopup="menu"
                    aria-expanded={saveMenuOpen}
                    disabled={createMutation.isPending || updateMutation.isPending}
                    data-testid="banner-layout-save-menu"
                  >
                    <ChevronIcon open={saveMenuOpen} />
                  </button>
                </span>
              ) : (
                // 読み込んでいない (白紙・プリセット由来): 単独ボタンで名前入力へ
                <button
                  type="button"
                  className="btn-modal-secondary"
                  onClick={openSaveName}
                  aria-haspopup="dialog"
                  aria-expanded={saveOpen}
                  disabled={createMutation.isPending}
                  data-testid="banner-layout-save-template"
                >
                  マイテンプレートとして保存
                </button>
              )}
              <Popover anchorRef={saveAnchorRef} open={saveMenuOpen && !!loadedTemplate} onClose={closeSaveMenu} className={styles.saveMenu} placement="above" align="right" role="menu" ariaLabel="その他の保存" inModal>
                <button type="button" role="menuitem" className={styles.saveMenuItem} onClick={openSaveName} data-testid="banner-layout-save-new">
                  名前をつけて保存…
                </button>
              </Popover>
              <Popover anchorRef={saveAnchorRef} open={saveOpen} onClose={closeSave} className={styles.savePopover} placement="above" role="dialog" ariaLabel="マイテンプレートとして保存" inModal>
                <input
                  type="text"
                  className={styles.input}
                  value={saveName}
                  maxLength={60}
                  placeholder="テンプレート名"
                  autoFocus
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isComposingEnter(e)) handleSave();
                  }}
                  aria-label="テンプレート名"
                />
                <button type="button" className="btn-modal-sm" onClick={handleSave} disabled={createMutation.isPending}>
                  保存
                </button>
              </Popover>
            </div>
            {/* 適用はブランドのサブカラー (利用者指示 2026-10-19) */}
            <button type="button" className="btn-modal-sub" onClick={handleApply} data-testid="banner-layout-apply">適用</button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={!!pendingTemplateAction}
        title={pendingTemplateAction ? { share: 'チームに共有しますか？', delete: 'テンプレートを削除しますか？' }[pendingTemplateAction.kind] : ''}
        message={
          pendingTemplateAction && (
            <>
              {pendingTemplateAction.kind === 'share' && (
                <>「{pendingTemplateAction.template.name}」の保存済みのレイアウトが画像として共有フォルダに入ります。<br />商品の枠は枠だけになります。</>
              )}
              {pendingTemplateAction.kind === 'delete' && <>「{pendingTemplateAction.template.name}」を削除します。この操作は元に戻せません。</>}
            </>
          )
        }
        confirmLabel={pendingTemplateAction ? { share: '共有する', delete: '削除' }[pendingTemplateAction.kind] : ''}
        cancelLabel="キャンセル"
        variant={pendingTemplateAction?.kind === 'delete' ? 'danger' : 'warning'}
        onConfirm={() => {
          const action = pendingTemplateAction;
          setPendingTemplateAction(null);
          if (!action) return;
          if (action.kind === 'share') shareMutation.mutate(action.template.id);
          else deleteMutation.mutate(action.template.id);
        }}
        onCancel={() => setPendingTemplateAction(null)}
      />
      <ConfirmDialog
        isOpen={!!pendingApply}
        title="アスペクト比を変更しますか？"
        message={
          pendingApply && (
            <>
              現在の設定は{effectiveAspect}です。このレイアウトを適用すると、生成するアスペクト比を{pendingApply.aspectRatio}に変更します。
              {ratioLocked && <><br />媒体規格・解像度の指定は解除されます。</>}
            </>
          )
        }
        confirmLabel="変更して適用"
        cancelLabel="戻る"
        variant="warning"
        onConfirm={() => {
          const layout = pendingApply;
          setPendingApply(null);
          if (layout) onApply(layout, loadedName);
        }}
        onCancel={() => setPendingApply(null)}
      />
    </div>,
    document.body,
  );
}
